import { useState, useRef, useEffect } from 'react';
import { useLocation } from 'wouter';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import api from '../lib/api';
import { createWatermarkedPhoto, createWatermarkedPhotoFromImage, type WatermarkData } from '../lib/watermark';
import { analyzePhotoQuality } from '../lib/photo-quality';
import { computeIntegrityHash, generateDeviceFingerprint, getNextNonce } from '../lib/photo-integrity';
import { getDB } from '../lib/db';
import { useAuth } from '../stores/auth';
import {
  ArrowLeft,
  Camera as CameraIcon,
  RotateCw,
  Check,
  X,
  Upload,
  Image as ImageIcon,
  AlertCircle
} from 'lucide-react';

type PhotoType = 'SHELF' | 'PRICE' | 'RUPTURE' | 'PLANOGRAM' | 'MATERIAL' | 'BEFORE' | 'AFTER';

const PHOTO_TYPES: { value: PhotoType; label: string }[] = [
  { value: 'SHELF', label: 'Gôndola' },
  { value: 'PRICE', label: 'Preço' },
  { value: 'RUPTURE', label: 'Ruptura' },
  { value: 'PLANOGRAM', label: 'Planograma' },
  { value: 'MATERIAL', label: 'Material POP' },
  { value: 'BEFORE', label: 'Antes' },
  { value: 'AFTER', label: 'Depois' },
];

const CATEGORIES = [
  { value: 'BEER', label: 'Cerveja' },
  { value: 'JUICE', label: 'Suco' },
  { value: 'SODA', label: 'Refrigerante' },
  { value: 'WATER', label: 'Água' },
  { value: 'ENERGY', label: 'Energético' },
  { value: 'OTHER', label: 'Outros' },
];

function getCurrentPosition(): Promise<GeolocationPosition> {
  return new Promise((resolve, reject) => {
    navigator.geolocation.getCurrentPosition(resolve, reject, {
      enableHighAccuracy: true,
      timeout: 10000,
      maximumAge: 30000,
    });
  });
}

interface VisitDetail {
  id: string;
  store_id: string;
  promoter_id: string;
  promoter_name: string;
  store_name: string;
  store_latitude: number | null;
  store_longitude: number | null;
  allow_gallery: boolean;
}

export default function Camera({ visitId }: { visitId: string }) {
  const [, setLocation] = useLocation();
  const queryClient = useQueryClient();
  const user = useAuth((s) => s.user);
  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Read URL search params for before/after pairing
  const urlParams = new URLSearchParams(window.location.search);
  const beforePhotoId = urlParams.get('before_photo_id');
  const initialType = urlParams.get('type') as PhotoType | null;

  const [stream, setStream] = useState<MediaStream | null>(null);
  const [capturedImage, setCapturedImage] = useState<string | null>(null);
  const [capturedBlob, setCapturedBlob] = useState<Blob | null>(null);
  const [captureMetadata, setCaptureMetadata] = useState<{
    hash: string;
    nonce: number;
    fingerprint: string;
    latitude: number;
    longitude: number;
    timestamp: string;
  } | null>(null);
  const [photoType, setPhotoType] = useState<PhotoType>(initialType || 'SHELF');
  const [category, setCategory] = useState('BEER');
  const [cameraError, setCameraError] = useState<string | null>(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [qualityIssues, setQualityIssues] = useState<string[]>([]);
  const [useFrontCamera, setUseFrontCamera] = useState(false);

  // Fetch visit details (store name, promoter name, coords, allow_gallery)
  const { data: visit } = useQuery<VisitDetail>({
    queryKey: ['visit', visitId],
    queryFn: () => api.get(`/visits/${visitId}`).then((r) => r.data.data),
  });

  const allowGallery = visit?.allow_gallery ?? false;

  // Initialize camera
  useEffect(() => {
    startCamera();
    return () => {
      stopCamera();
    };
  }, [useFrontCamera]);

  const startCamera = async () => {
    setCameraError(null);
    try {
      const mediaStream = await navigator.mediaDevices.getUserMedia({
        video: {
          facingMode: useFrontCamera ? 'user' : 'environment',
          width: { ideal: 1920 },
          height: { ideal: 1080 },
        },
        audio: false,
      });

      setStream(mediaStream);
      if (videoRef.current) {
        videoRef.current.srcObject = mediaStream;
      }
    } catch (error: any) {
      console.error('Camera error:', error);
      if (error.name === 'NotAllowedError' || error.name === 'PermissionDeniedError') {
        setCameraError('Permissão de câmera negada. Habilite nas configurações.');
      } else if (error.name === 'NotFoundError') {
        setCameraError('Câmera não encontrada no dispositivo.');
      } else {
        setCameraError('Erro ao acessar câmera. Use o botão de galeria.');
      }
    }
  };

  const stopCamera = () => {
    if (stream) {
      stream.getTracks().forEach((track) => track.stop());
      setStream(null);
    }
  };

  const capturePhoto = async () => {
    if (!videoRef.current || !canvasRef.current) return;

    const video = videoRef.current;
    const canvas = canvasRef.current;
    const now = new Date();
    const timestamp = now.toISOString();

    // Get GPS position (fallback to store coords if geolocation fails)
    let latitude = visit?.store_latitude ?? 0;
    let longitude = visit?.store_longitude ?? 0;
    try {
      const pos = await getCurrentPosition();
      latitude = pos.coords.latitude;
      longitude = pos.coords.longitude;
    } catch {
      console.warn('Geolocation failed, using store coordinates as fallback');
    }

    // Build watermark data
    const watermarkData: WatermarkData = {
      promoterName: visit?.promoter_name ?? user?.name ?? 'Promotor',
      storeName: visit?.store_name ?? 'Loja',
      companyLogoUrl: user?.company?.logo ?? undefined,
      latitude,
      longitude,
      timestamp: now,
    };

    // Check photo quality on RAW frame BEFORE watermarking
    // (watermark text would affect brightness/blur analysis)
    const ctx = canvas.getContext('2d');
    if (ctx) {
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
      ctx.drawImage(video, 0, 0);
    }
    const quality = analyzePhotoQuality(canvas);
    if (!quality.passed) {
      setQualityIssues(quality.issues);
      return;
    }
    setQualityIssues([]);

    // Create watermarked photo blob (quality already validated on raw frame)
    const blob = await createWatermarkedPhoto(video, canvas, watermarkData);

    // Compute integrity hash
    const fingerprint = generateDeviceFingerprint();
    const nonce = getNextNonce();
    const hash = await computeIntegrityHash(blob, {
      latitude,
      longitude,
      timestamp,
      deviceFingerprint: fingerprint,
      promoterId: visit?.promoter_id ?? user?.id ?? '',
      nonce,
    });

    // Store blob and metadata
    setCapturedBlob(blob);
    setCaptureMetadata({ hash, nonce, fingerprint, latitude, longitude, timestamp });

    // Create preview URL
    const previewUrl = URL.createObjectURL(blob);
    setCapturedImage(previewUrl);
    stopCamera();
  };

  const retakePhoto = () => {
    if (capturedImage) {
      URL.revokeObjectURL(capturedImage);
    }
    setCapturedImage(null);
    setCapturedBlob(null);
    setCaptureMetadata(null);
    setUploadProgress(0);
    setQualityIssues([]);
    startCamera();
  };

  const handleFileSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Load the selected file into an Image element
    const objectUrl = URL.createObjectURL(file);
    const img = new Image();
    img.src = objectUrl;
    await new Promise<void>((resolve, reject) => {
      img.onload = () => resolve();
      img.onerror = () => reject(new Error('Failed to load image'));
    });

    // Ensure we have a canvas to work with (create a temporary one if needed)
    const canvas = canvasRef.current ?? document.createElement('canvas');

    // Run quality check on the raw image before watermarking
    canvas.width = img.naturalWidth;
    canvas.height = img.naturalHeight;
    const ctx = canvas.getContext('2d');
    if (ctx) ctx.drawImage(img, 0, 0);
    const quality = analyzePhotoQuality(canvas);
    if (!quality.passed) {
      setQualityIssues(quality.issues);
      URL.revokeObjectURL(objectUrl);
      return;
    }
    setQualityIssues([]);

    // Get GPS position (fallback to store coords)
    const now = new Date();
    const timestamp = now.toISOString();
    let latitude = visit?.store_latitude ?? 0;
    let longitude = visit?.store_longitude ?? 0;
    try {
      const pos = await getCurrentPosition();
      latitude = pos.coords.latitude;
      longitude = pos.coords.longitude;
    } catch {
      console.warn('Geolocation failed, using store coordinates as fallback');
    }

    // Build watermark data
    const watermarkData: WatermarkData = {
      promoterName: visit?.promoter_name ?? user?.name ?? 'Promotor',
      storeName: visit?.store_name ?? 'Loja',
      companyLogoUrl: user?.company?.logo ?? undefined,
      latitude,
      longitude,
      timestamp: now,
    };

    // Apply watermark using the image element
    const blob = await createWatermarkedPhotoFromImage(img, canvas, watermarkData);
    URL.revokeObjectURL(objectUrl);

    // Compute integrity hash
    const fingerprint = generateDeviceFingerprint();
    const nonce = getNextNonce();
    const hash = await computeIntegrityHash(blob, {
      latitude,
      longitude,
      timestamp,
      deviceFingerprint: fingerprint,
      promoterId: visit?.promoter_id ?? user?.id ?? '',
      nonce,
    });

    // Store blob and metadata, show preview
    setCapturedBlob(blob);
    setCaptureMetadata({ hash, nonce, fingerprint, latitude, longitude, timestamp });
    const previewUrl = URL.createObjectURL(blob);
    setCapturedImage(previewUrl);
    stopCamera();
  };

  const uploadMutation = useMutation({
    mutationFn: async () => {
      if (!capturedBlob || !captureMetadata) throw new Error('Nenhuma foto capturada');

      // Create form data with integrity fields
      const formData = new FormData();
      formData.append('photo', capturedBlob, `photo_${Date.now()}.jpg`);
      formData.append('visit_id', visitId);
      formData.append('type', photoType);
      formData.append('category', category);
      formData.append('integrity_hash', captureMetadata.hash);
      formData.append('device_nonce', String(captureMetadata.nonce));
      formData.append('device_fingerprint', captureMetadata.fingerprint);
      formData.append('latitude', String(captureMetadata.latitude));
      formData.append('longitude', String(captureMetadata.longitude));
      formData.append('capture_timestamp', captureMetadata.timestamp);

      // Upload with progress
      const uploadResponse = await api.post('/photos', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        onUploadProgress: (progressEvent) => {
          const progress = progressEvent.total
            ? Math.round((progressEvent.loaded * 100) / progressEvent.total)
            : 0;
          setUploadProgress(progress);
        },
      });

      return uploadResponse.data.data;
    },
    onSuccess: async (data: any) => {
      // Auto-pair if this is an AFTER photo linked to a BEFORE/RUPTURE
      if (beforePhotoId && data?.id) {
        try {
          await api.post(`/photos/${data.id}/pair`, { before_photo_id: beforePhotoId });
        } catch (pairErr) {
          console.error('Failed to pair photos:', pairErr);
        }
      }
      queryClient.invalidateQueries({ queryKey: ['visit', 'active', visitId] });
      queryClient.invalidateQueries({ queryKey: ['photos', visitId] });
      queryClient.invalidateQueries({ queryKey: ['photos', 'unpaired'] });
      setLocation(`/visit/${visitId}`);
    },
    onError: async (error: any) => {
      // Offline fallback: save to IndexedDB
      if (!navigator.onLine || error.code === 'ERR_NETWORK') {
        try {
          const db = await getDB();
          await db.put('photos', {
            id: `photo-${Date.now()}-${Math.random().toString(36).slice(2)}`,
            visit_id: visitId,
            blob: capturedBlob!,
            type: photoType,
            category: category,
            synced: false,
            created_at: new Date().toISOString(),
            integrity_hash: captureMetadata!.hash,
            device_nonce: captureMetadata!.nonce,
            device_fingerprint: captureMetadata!.fingerprint,
            latitude: captureMetadata!.latitude,
            longitude: captureMetadata!.longitude,
            capture_metadata: { timestamp: captureMetadata!.timestamp },
          });
          setLocation(`/visit/${visitId}`);
          return;
        } catch (dbError) {
          console.error('Failed to save photo offline:', dbError);
        }
      }
      alert(error.response?.data?.error?.message || 'Erro ao enviar foto');
      setUploadProgress(0);
    },
  });

  const handleUpload = () => {
    uploadMutation.mutate();
  };

  const toggleCamera = () => {
    setUseFrontCamera(!useFrontCamera);
  };

  return (
    <div className="flex flex-col min-h-screen bg-black">
      {/* Header */}
      <header className="absolute top-0 left-0 right-0 z-10 bg-gradient-to-b from-black/70 to-transparent text-white p-4">
        <div className="flex items-center justify-between">
          <button
            onClick={() => setLocation(`/visit/${visitId}`)}
            className="p-2 -ml-2 rounded-full hover:bg-white/20"
          >
            <ArrowLeft className="h-6 w-6" />
          </button>
          <h1 className="text-lg font-bold">Capturar Foto</h1>
          <div className="w-10"></div>
        </div>
      </header>

      {/* Camera/Preview Area */}
      <main className="flex-1 relative flex items-center justify-center">
        {cameraError ? (
          <div className="text-center p-4">
            <AlertCircle className="h-16 w-16 text-yellow-500 mx-auto mb-4" />
            <p className="text-white text-sm mb-4">{cameraError}</p>
            {allowGallery ? (
              <button
                onClick={() => fileInputRef.current?.click()}
                className="flex items-center gap-2 px-6 py-3 bg-white text-gray-900 rounded-lg font-semibold mx-auto"
              >
                <ImageIcon className="h-5 w-5" />
                Escolher da Galeria
              </button>
            ) : (
              <p className="text-white/60 text-xs">Galeria desabilitada. Verifique as permissoes da camera.</p>
            )}
          </div>
        ) : capturedImage ? (
          <img
            src={capturedImage}
            alt="Foto capturada"
            className="max-w-full max-h-full object-contain"
          />
        ) : (
          <>
            <video
              ref={videoRef}
              autoPlay
              playsInline
              muted
              className="max-w-full max-h-full object-contain"
            />
            <canvas ref={canvasRef} className="hidden" />
          </>
        )}

        {/* Quality feedback */}
        {qualityIssues.length > 0 && (
          <div className="absolute inset-x-4 bottom-28 bg-red-600/90 rounded-xl p-4 text-white z-20">
            <p className="font-semibold text-sm mb-2">Foto rejeitada — problemas detectados:</p>
            <ul className="space-y-1">
              {qualityIssues.map((issue, i) => (
                <li key={i} className="text-sm flex items-start gap-2">
                  <span className="mt-0.5">&#9888;</span>
                  <span>{issue}</span>
                </li>
              ))}
            </ul>
            <button
              onClick={() => { setQualityIssues([]); startCamera(); }}
              className="mt-3 w-full bg-white text-red-600 font-semibold py-2 rounded-lg text-sm"
            >
              Tirar Novamente
            </button>
          </div>
        )}
      </main>

      {/* Controls */}
      <div className="bg-gradient-to-t from-black/90 to-transparent p-4 space-y-4">
        {capturedImage ? (
          <>
            {/* Photo Type Selector */}
            <div className="space-y-2">
              <label className="text-white text-sm font-medium">Tipo de Foto</label>
              <select
                value={photoType}
                onChange={(e) => setPhotoType(e.target.value as PhotoType)}
                className="w-full px-4 py-3 bg-white/10 text-white rounded-lg border border-white/20 focus:outline-none focus:border-white/40"
                disabled={uploadMutation.isPending}
              >
                {PHOTO_TYPES.map((type) => (
                  <option key={type.value} value={type.value} className="bg-gray-900">
                    {type.label}
                  </option>
                ))}
              </select>
            </div>

            {/* Category Selector */}
            <div className="space-y-2">
              <label className="text-white text-sm font-medium">Categoria</label>
              <select
                value={category}
                onChange={(e) => setCategory(e.target.value)}
                className="w-full px-4 py-3 bg-white/10 text-white rounded-lg border border-white/20 focus:outline-none focus:border-white/40"
                disabled={uploadMutation.isPending}
              >
                {CATEGORIES.map((cat) => (
                  <option key={cat.value} value={cat.value} className="bg-gray-900">
                    {cat.label}
                  </option>
                ))}
              </select>
            </div>

            {/* Upload Progress */}
            {uploadProgress > 0 && uploadProgress < 100 && (
              <div className="space-y-2">
                <div className="flex items-center justify-between text-white text-sm">
                  <span>Enviando...</span>
                  <span>{uploadProgress}%</span>
                </div>
                <div className="h-2 bg-white/20 rounded-full overflow-hidden">
                  <div
                    className="h-full bg-primary transition-all"
                    style={{ width: `${uploadProgress}%` }}
                  />
                </div>
              </div>
            )}

            {/* Action Buttons */}
            <div className="grid grid-cols-2 gap-3">
              <button
                onClick={retakePhoto}
                disabled={uploadMutation.isPending}
                className="flex items-center justify-center gap-2 px-6 py-4 bg-white/10 text-white rounded-xl font-semibold border border-white/20 active:bg-white/20 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <X className="h-5 w-5" />
                Refazer
              </button>
              <button
                onClick={handleUpload}
                disabled={uploadMutation.isPending}
                className="flex items-center justify-center gap-2 px-6 py-4 bg-primary text-white rounded-xl font-semibold active:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {uploadMutation.isPending ? (
                  <>
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                    <span>Enviando...</span>
                  </>
                ) : (
                  <>
                    <Check className="h-5 w-5" />
                    Enviar
                  </>
                )}
              </button>
            </div>
          </>
        ) : (
          <>
            {/* Camera Controls */}
            <div className="flex items-center justify-center gap-4">
              {/* Gallery Button — only if promoter has allow_gallery */}
              {allowGallery ? (
                <button
                  onClick={() => fileInputRef.current?.click()}
                  className="p-4 rounded-full bg-white/10 border border-white/20 active:bg-white/20"
                >
                  <ImageIcon className="h-6 w-6 text-white" />
                </button>
              ) : (
                <div className="p-4 rounded-full" />
              )}

              {/* Capture Button */}
              <button
                onClick={capturePhoto}
                disabled={!stream}
                className="p-6 rounded-full bg-white border-4 border-white/30 active:scale-95 transition-transform disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <CameraIcon className="h-8 w-8 text-gray-900" />
              </button>

              {/* Flip Camera Button */}
              <button
                onClick={toggleCamera}
                className="p-4 rounded-full bg-white/10 border border-white/20 active:bg-white/20"
              >
                <RotateCw className="h-6 w-6 text-white" />
              </button>
            </div>

            {/* Quick Type Selector */}
            <div className="flex items-center gap-2 overflow-x-auto pb-2">
              {PHOTO_TYPES.map((type) => (
                <button
                  key={type.value}
                  onClick={() => setPhotoType(type.value)}
                  className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-colors ${
                    photoType === type.value
                      ? 'bg-primary text-white'
                      : 'bg-white/10 text-white border border-white/20'
                  }`}
                >
                  {type.label}
                </button>
              ))}
            </div>
          </>
        )}
      </div>

      {/* Hidden File Input — only if gallery is allowed */}
      {allowGallery && (
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          onChange={handleFileSelect}
          className="hidden"
        />
      )}
    </div>
  );
}
